import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { CheckBox } from '@gitbook/react-uikit';

import { useContentKitConfirm } from './ContentKitConfirm';
import type { ContentKitElementProps, ContentKitElementRef } from './types';

/**
 * Renderer for a checkbox element.
 * It is connected to an array state containing `element.value` if checked.
 */
export function ContentKitCheckbox({
    ref,
    ...props
}: ContentKitElementProps<api.ContentKitCheckbox> & {
    ref?: React.Ref<ContentKitElementRef>;
}): React.ReactElement {
    const { element, localState, setLocalState } = props;

    const valueSet = React.useMemo(() => {
        return new Set<number | string>(localState[element.state] || []);
    }, [localState, element.state]);

    const value = valueSet.has(element.value);

    const [confirm, wrapConfirm] = useContentKitConfirm();

    const press = () => {
        wrapConfirm(value ? undefined : element.confirm, () => {
            setLocalState((prev) => {
                const newValue = new Set<number | string>(prev[element.state] || []);
                if (!value) {
                    newValue.add(element.value);
                } else {
                    newValue.delete(element.value);
                }
                return {
                    ...prev,
                    [element.state]: Array.from(newValue),
                };
            });
        });
    };

    React.useImperativeHandle(ref, () => ({
        press,
    }));

    return (
        <>
            <CheckBox value={value} onValueChange={press} />
            {confirm}
        </>
    );
}
